/**
 * QR Code Checker Page
 * Public page for USLS internal users to scan/enter a CTA QR code and see its details.
 * Informational only - no redemption triggered.
 */

import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { anonSupabase } from "../services/supabase";
import { formatDate } from "../utils/formatters";

interface CTADetails {
  source: "organization" | "event";
  id: string;
  name: string;
  cta_type: string;
  reward_type: string;
  voucher_amount: number | null;
  voucher_start_date: string | null;
  voucher_end_date: string | null;
  is_active: boolean;
  parameters: Record<string, unknown> | null;
  generated_url: string | null;
  organization_name: string | null;
  organization_id: string | null;
  location_name?: string | null;
  location_id?: string | null;
  state?: string | null;
  event_name?: string | null;
  event_id?: string | null;
  event_date?: string | null;
}

const QRCodeChecker: React.FC = () => {
  const [input, setInput] = useState("");
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ctaDetails, setCTADetails] = useState<CTADetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const html5QrcodeRef = useRef<Html5Qrcode | null>(null);
  const scannerRef = useRef<HTMLDivElement>(null);

  // Extract ctaid from URL or bare UUID
  const extractCtaId = (value: string): string | null => {
    const trimmed = value.trim();

    // Check if it's a UUID directly
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidRegex.test(trimmed)) {
      return trimmed;
    }

    // Try to extract ctaid from URL
    try {
      const url = new URL(trimmed);
      const ctaid = url.searchParams.get("ctaid");
      if (ctaid && uuidRegex.test(ctaid)) {
        return ctaid;
      }
    } catch {
      // Not a URL
    }

    return null;
  };

  const lookupCTA = async (value: string) => {
    const ctaId = extractCtaId(value);
    if (!ctaId) {
      setError("Invalid input. Please enter a valid CTA URL or UUID.");
      setCTADetails(null);
      setSearched(true);
      return;
    }

    setLoading(true);
    setError(null);
    setCTADetails(null);
    setSearched(true);

    try {
      const { data, error: rpcError } = await anonSupabase.rpc(
        "lookup_cta_public",
        { p_cta_id: ctaId },
      );

      if (rpcError) {
        throw rpcError;
      }

      if (!data) {
        setError(
          "CTA not found. Please check the QR code or URL and try again.",
        );
      } else {
        setCTADetails(data as CTADetails);
      }
    } catch (err: unknown) {
      console.error("Lookup error:", err);
      setError("An error occurred while looking up the CTA. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      lookupCTA(input);
    }
  };

  // QR Scanner
  const startScanner = async () => {
    if (!scannerRef.current) {
      return;
    }

    try {
      // Show the scanner div first, then wait for DOM update
      setScanning(true);
      await new Promise((resolve) => setTimeout(resolve, 100));

      const html5Qrcode = new Html5Qrcode("qr-reader");
      html5QrcodeRef.current = html5Qrcode;

      await html5Qrcode.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          stopScanner();
          setInput(decodedText);
          lookupCTA(decodedText);
        },
        () => {
          // Ignore scan failures
        },
      );
    } catch (err) {
      console.error("Scanner error:", err);
      setScanning(false);
      setError("Unable to start camera. Please enter the URL manually.");
    }
  };

  const stopScanner = async () => {
    if (html5QrcodeRef.current) {
      try {
        await html5QrcodeRef.current.stop();
        html5QrcodeRef.current.clear();
      } catch {
        // Ignore cleanup errors
      }
      html5QrcodeRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const handleReset = () => {
    setInput("");
    setCTADetails(null);
    setError(null);
    setSearched(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                CTA QR Code Checker
              </h1>
              <p className="text-sm text-gray-500">
                Scan or enter a CTA URL to view its details
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Scanner Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* QR Scanner Area */}
          <div className="mb-4">
            <div
              id="qr-reader"
              ref={scannerRef}
              className={scanning ? "mb-4" : "hidden"}
            />
            <button
              type="button"
              onClick={scanning ? stopScanner : startScanner}
              className={`w-full py-3 px-4 rounded-md text-sm font-medium flex items-center justify-center gap-2 ${
                scanning
                  ? "bg-red-100 text-red-700 hover:bg-red-200"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    scanning
                      ? "M6 18L18 6M6 6l12 12"
                      : "M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  }
                />
                {!scanning && (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                )}
              </svg>
              {scanning ? "Stop Scanner" : "Scan QR Code"}
            </button>
          </div>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">
                or enter manually
              </span>
            </div>
          </div>

          {/* Manual Input */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste CTA URL or UUID..."
              className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="flex-1 bg-primary-600 text-white py-3 px-4 rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Looking up...
                  </>
                ) : (
                  "Look Up CTA"
                )}
              </button>
              {searched && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-red-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Results Card */}
        {ctaDetails && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Card Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {ctaDetails.name}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {ctaDetails.source === "organization"
                      ? "Organization CTA"
                      : "Event CTA"}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    ctaDetails.is_active
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {ctaDetails.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {/* Card Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Organization */}
              {ctaDetails.organization_name && (
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-sm font-medium text-gray-500">
                    Organization
                  </dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {ctaDetails.organization_name}
                  </dd>
                </div>
              )}

              {/* Location (org CTAs) */}
              {ctaDetails.location_name && (
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-sm font-medium text-gray-500">
                    Location
                  </dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {ctaDetails.location_name}
                  </dd>
                </div>
              )}

              {/* State */}
              {ctaDetails.state && (
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-sm font-medium text-gray-500">State</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {ctaDetails.state}
                  </dd>
                </div>
              )}

              {/* Event (event CTAs) */}
              {ctaDetails.event_name && (
                <div className="grid grid-cols-3 gap-2">
                  <dt className="text-sm font-medium text-gray-500">Event</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {ctaDetails.event_name}
                    {ctaDetails.event_date && (
                      <span className="text-gray-500 ml-2">
                        ({formatDate(ctaDetails.event_date)})
                      </span>
                    )}
                  </dd>
                </div>
              )}

              <div className="border-t border-gray-100 pt-4">
                {/* CTA Type */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <dt className="text-sm font-medium text-gray-500">
                    CTA Type
                  </dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {ctaDetails.cta_type || "N/A"}
                  </dd>
                </div>

                {/* Reward Type */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <dt className="text-sm font-medium text-gray-500">
                    Reward Type
                  </dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {ctaDetails.reward_type || "N/A"}
                  </dd>
                </div>

                {/* Voucher Amount */}
                {ctaDetails.voucher_amount !== null &&
                  ctaDetails.voucher_amount !== undefined && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <dt className="text-sm font-medium text-gray-500">
                        Voucher Amount
                      </dt>
                      <dd className="text-sm text-gray-900 col-span-2 font-medium">
                        ${Number(ctaDetails.voucher_amount).toFixed(2)}
                      </dd>
                    </div>
                  )}

                {/* Valid Date Range */}
                {(ctaDetails.voucher_start_date ||
                  ctaDetails.voucher_end_date) && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <dt className="text-sm font-medium text-gray-500">
                      Valid Dates
                    </dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {ctaDetails.voucher_start_date
                        ? formatDate(ctaDetails.voucher_start_date)
                        : "N/A"}{" "}
                      &rarr;{" "}
                      {ctaDetails.voucher_end_date
                        ? formatDate(ctaDetails.voucher_end_date)
                        : "No end date"}
                    </dd>
                  </div>
                )}
              </div>

              {/* URL Parameters */}
              {ctaDetails.parameters &&
                Object.keys(ctaDetails.parameters).length > 0 && (
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      URL Parameters
                    </h3>
                    <div className="bg-gray-50 rounded-md p-3 space-y-1">
                      {Object.entries(ctaDetails.parameters).map(
                        ([key, value]) => (
                          <div key={key} className="flex gap-2 text-sm">
                            <span className="font-mono text-gray-600">
                              {key}:
                            </span>
                            <span className="text-gray-900">
                              {String(value)}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}

              {/* Generated URL */}
              {ctaDetails.generated_url && (
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Generated URL
                  </h3>
                  <p className="text-xs text-gray-700 bg-gray-50 rounded-md p-3 font-mono break-all">
                    {ctaDetails.generated_url}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeChecker;
